import { Button, Callout, Card, Heading, ProgressBar, StatCard, StatCardGroup } from '@humanforce/mango-ui-react';
import type { PayRunDetailDto } from './payRunsApiTypes';
import type { PayRunPopulationStatus } from './payRunPopulationApiTypes';
import type { CSSProperties } from 'react';
import { formatSystemValue, isSafePayRunPresentationState } from './payRunsModel';
import type { PayRunSummaryLeadCard } from './payRunTimelineModel';
import { PayRunInlineCallout } from './PayRunInlineCallout';
import './PayRunDetailSummary.css';

const metricCardCount = 6;

interface PayRunDetailHeaderProps {
  payRun: PayRunDetailDto;
  onLoadPopulation: () => void;
  transitioning: boolean;
  actionBusy: boolean;
  calculationBusy: boolean;
  calculationError: string | null;
  onCalculate: () => void;
  onRefreshCalculation: () => void;
}

const money = (value: number): string =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);

const summary = (
  payRun: PayRunDetailDto,
  status: PayRunPopulationStatus | null,
) => {
  const calculated = payRun.calculation?.status === 'complete';
  return [
  { label: 'Employees', value: status?.employeeCount ?? payRun.employeeCount ?? 0, icon: 'person' as const },
  { label: 'Gross Pay', value: calculated ? money(payRun.gross) : '—', icon: 'dollar-sign' as const },
  { label: 'Tax', value: calculated ? money(payRun.tax) : '—', icon: 'percent' as const },
  { label: 'STSL', value: calculated ? money(payRun.stsl) : '—', icon: 'percent' as const },
  { label: 'Super', value: calculated ? money(payRun.super) : '—', icon: 'achievement' as const },
  { label: 'Net Pay', value: calculated ? money(payRun.netPay) : '—', icon: 'ledger' as const },
  ];
};

export const PayRunDetailHeader = ({
  payRun,
  onLoadPopulation,
  transitioning,
  actionBusy,
  calculationBusy,
  calculationError,
  onCalculate,
  onRefreshCalculation,
}: PayRunDetailHeaderProps) => {
  const calculation = payRun.calculation;
  const calculationRunning = calculation?.status === 'admitted'
    || calculation?.status === 'calculating'
    || calculation?.status === 'cancellation_requested';
  const safeState = isSafePayRunPresentationState(payRun);
  const processedWorkerCount = calculation
    ? calculation.succeededWorkerCount + calculation.failedWorkerCount + calculation.reviewRequiredWorkerCount
    : 0;
  const calculationProgressPercent = calculation && calculation.workerCount > 0
    ? Math.round((processedWorkerCount / calculation.workerCount) * 100)
    : 0;

  return (
    <div className="mgo-stack mgo-gap-l">
      <div className="mgo-grid mgo-grid-cols-1 mgo-gap-l">
        <div className="mgo-cluster mgo-gap-s mgo-justify-end">
          {safeState && payRun.meta.actions.includes('load_population_sources') && (
            <Button
              label="Load Pay Run data"
              variant="secondary"
              icon="upload"
              disabled={transitioning || actionBusy}
              onClick={onLoadPopulation}
            />
          )}
          {safeState && (payRun.meta.actions.includes('calculate') || payRun.meta.actions.includes('retry_calculation')) && (
            <Button
              label={payRun.meta.actions.includes('retry_calculation') ? 'Retry calculation' : 'Calculate Pay Run'}
              icon="payroll-dollar"
              loading={calculationBusy || calculationRunning}
              disabled={transitioning || actionBusy || calculationRunning}
              onClick={onCalculate}
            />
          )}
        </div>
      </div>

      {calculationError && (
        <Callout
          tone="critical"
          title={calculationRunning ? 'Calculation status unavailable' : 'Calculation not started'}
          action={calculationRunning
            ? <Button label="Retry status" size="small" variant="secondary" loading={calculationBusy} onClick={onRefreshCalculation} />
            : undefined}
        >
          {calculationError}
        </Callout>
      )}
      {calculationRunning && calculation && (
        <Card>
          <div className="mgo-stack mgo-gap-xs">
            <div className="mgo-cluster mgo-justify-between">
              <span className="mgo-weight-strong">
                {calculation.status === 'admitted'
                  ? 'Calculation queued'
                  : calculation.status === 'cancellation_requested'
                    ? 'Calculation cancellation requested'
                    : `Calculating — ${processedWorkerCount} of ${calculation.workerCount} employees processed`}
              </span>
              {calculation.workerCount > 0 && <span data-testid="pay-run-calculation-progress-percent">{calculationProgressPercent}%</span>}
            </div>
            {calculation.workerCount > 0 && (
              <ProgressBar
                value={calculationProgressPercent}
                label={`Calculation progress: ${processedWorkerCount} of ${calculation.workerCount} employees processed`}
                testId="pay-run-calculation-progress"
              />
            )}
          </div>
        </Card>
      )}
      {calculation?.status === 'complete' && (
        <PayRunInlineCallout
          tone="positive"
          title="Calculation complete"
          body={`${calculation.workerCount} of ${calculation.workerCount} employees processed.`}
        />
      )}
      {calculation && ['failed', 'review_required'].includes(calculation.status) && (
        <PayRunInlineCallout
          tone="critical"
          title={calculation.status === 'failed' ? 'Calculation failed' : 'Calculation needs review'}
          body={payRun.meta.actions.includes('retry_calculation')
            ? 'Review the calculation issues, then use Retry calculation.'
            : 'Review the calculation issues before continuing.'}
        />
      )}
      {calculation && ['failed', 'review_required'].includes(calculation.status) && calculation.issues.length > 0 && (
        <Card>
          <div className="mgo-stack mgo-gap-s">
            <Heading level={2}>Calculation issues</Heading>
            <ul className="mgo-stack mgo-gap-xs" style={{ margin: 0 }}>
              {calculation.issues.map((issue, index) => (
                <li key={`${issue.code}-${issue.workerId ?? 'run'}-${index}`}>
                  <span className="mgo-weight-strong">{formatSystemValue(issue.code)}</span>
                  {issue.workerId ? ` · Employee ${issue.workerId}` : ''}: {issue.detail}
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}
    </div>
  );
};

export const PayRunDetailSummary = ({
  payRun,
  populationStatus,
  leadCards,
}: {
  payRun: PayRunDetailDto;
  populationStatus: PayRunPopulationStatus | null;
  leadCards: PayRunSummaryLeadCard[];
}) => (
  <section aria-label="Pay Run summary" className="pay-run-summary">
    <div
      className="pay-run-summary__scroll"
      style={{ '--pay-run-summary-columns': leadCards.length + metricCardCount } as CSSProperties}
    >
      <StatCardGroup className="pay-run-summary-row">
        {leadCards.map((card) => (
          <StatCard
            key={card.id}
            testId={`pay-run-summary-${card.id}`}
            label={card.label}
            value={card.value}
            icon={card.icon}
            tone={card.tone}
          />
        ))}
        {summary(payRun, populationStatus).map((item) => (
          <StatCard key={item.label} label={item.label} value={item.value} icon={item.icon} />
        ))}
      </StatCardGroup>
    </div>
  </section>
);
